// FieldIQ — Shared voice recording hook, submission + voice topic constants

import { useState, useEffect, useRef } from "react";
import { FIELDIQ_API_URL, authFetch, getToken } from "./api";
import { queueOfflineWrite } from "./offline";
import { saveAudioBlob } from "./storage";

export const VOICE_SUBMIT_ERROR =
  "Your message couldn't be sent. Check your connection and tap Send to try again.";

export const fmtVoiceTime = (s) =>
  `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export const useVoiceRecorder = (onAutoStop) => {
  const [elapsed, setElapsed] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const timerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const onAutoStopRef = useRef(onAutoStop);
  onAutoStopRef.current = onAutoStop;

  const _stopRecorder = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  };

  // Returns true when recording actually started.
  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      alert("Audio recording isn't supported on this device.");
      return false;
    }
    try {
      chunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/mp4";
      const mr = new MediaRecorder(stream, { mimeType });
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        setAudioBlob(new Blob(chunksRef.current, { type: mimeType }));
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setAudioBlob(null);
    } catch {
      alert("Microphone permission denied.");
      return false;
    }
    setElapsed(0);
    timerRef.current = setInterval(() => {
      setElapsed((s) => {
        if (s + 1 >= VOICE_MAX_SECONDS) {
          clearInterval(timerRef.current);
          _stopRecorder();
          onAutoStopRef.current?.();
          return VOICE_MAX_SECONDS;
        }
        return s + 1;
      });
    }, 1000);
    return true;
  };

  const stop = () => {
    clearInterval(timerRef.current);
    _stopRecorder();
  };

  const reset = () => {
    clearInterval(timerRef.current);
    _stopRecorder();
    chunksRef.current = [];
    setAudioBlob(null);
    setElapsed(0);
  };

  useEffect(
    () => () => {
      clearInterval(timerRef.current);
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    },
    [],
  );

  return { elapsed, audioBlob, start, stop, reset };
};

export const submitVoiceMessage = async ({
  audioBlob,
  facility,
  isAnon,
  name,
  phone,
  selectedTopics,
  elapsed,
}) => {
  const topicsText = [...selectedTopics].join(", ");
  const messageText = topicsText
    ? `Topics: ${topicsText}. Duration: ${fmtVoiceTime(elapsed)}.`
    : `Duration: ${fmtVoiceTime(elapsed)}.`;
  const voicePayload = {
    facility_id: facility?.facility_id || null,
    voter_name: isAnon ? null : name.trim() || null,
    voter_phone: isAnon ? null : phone.trim() || null,
    message_text: messageText,
  };

  // Offline: keep the audio locally and queue the submission for sync
  if (!navigator.onLine) {
    if (audioBlob) {
      await saveAudioBlob(new Date().toISOString(), audioBlob);
    }
    await queueOfflineWrite(
      "/fieldiq/voice-messages",
      "POST",
      JSON.stringify(voicePayload),
    );
    return { ok: true, queued: true };
  }

  try {
    // 1. Upload audio into the Whisper pipeline
    let audioActivityId = null;
    if (audioBlob) {
      const fd = new FormData();
      const ext = audioBlob.type.includes("webm") ? "webm" : "mp4";
      fd.append("audio", audioBlob, `voter-voice.${ext}`);
      fd.append("facility_id", facility?.facility_id || "");
      fd.append("interaction_type", "voter_voice");
      fd.append(
        "person_name",
        isAnon ? "Anonymous" : name.trim() || "Anonymous",
      );
      fd.append("notes", topicsText);
      const token = getToken();
      const resp = await fetch(`${FIELDIQ_API_URL}/interactions/audio`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!resp.ok) throw new Error("audio upload failed");
      audioActivityId = (await resp.json()).id;
    }

    // 2. Create the voice_submissions row linked to the audio activity
    const result = await authFetch("/fieldiq/voice-messages", {
      method: "POST",
      body: JSON.stringify({
        ...voicePayload,
        audio_activity_id: audioActivityId,
      }),
    });
    if (result?.error) throw new Error(result.error);
    return { ok: true };
  } catch {
    return { ok: false };
  }
};

// ═══════════════════════════════════════════════════════════════════
// VOTER VOICE — launched from Log Interaction
// 5 steps: handoff → pitch → record → tag+identity → confirmation
// ═══════════════════════════════════════════════════════════════════

export const VOICE_TOPICS = [
  { icon: "💧", label: "Water Rights" },
  { icon: "💵", label: "Taxes" },
  { icon: "🏛️", label: "Elections" },
  { icon: "🛡️", label: "Border Security" },
  { icon: "🎓", label: "Education" },
  { icon: "🏥", label: "Healthcare" },
  { icon: "⚖️", label: "Crime & Safety" },
  { icon: "⚡", label: "Energy" },
  { icon: "🏘️", label: "Local Issues" },
  { icon: "✏️", label: "Other" },
];

export const SLUG_EMOJI = {
  "border-security": "🛡️",
  "tax-reform": "💵",
  education: "🎓",
  "water-rights": "💧",
  "crime-safety": "⚖️",
  "energy-policy": "⚡",
  elections: "🏛️",
  healthcare: "🏥",
  "local-issues": "🏘️",
};

export const WAVEFORM_HEIGHTS = [
  8, 16, 12, 24, 32, 20, 40, 28, 44, 24, 36, 16, 28, 40, 32, 20, 12, 24, 36, 28,
  16, 32, 24, 40, 20, 28, 16, 12, 32, 24, 16, 8,
];
export const VOICE_MAX_SECONDS = 30;
